import { useRef, useState } from 'react';
import { Modal, Spin, message } from 'antd';
import ModalHeader from '../../../../components/atoms/ModalHeader';
import FormField from '../../../../components/FormField/FormField';
import Button from '../../../../components/atoms/Button';
import FormWrapper from '../../../../components/categories/forms/FormWrapper';
import { z } from 'zod';
import { Category } from '../../../../types/lookups';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import VariantsSelector from './VariantsSelector';
import {
  useUpdateOperatorCategoryMutation,
  useUploadOperatorCategoryDocumentMutation,
} from '../../../../store/services/lookups';
import { field } from '../../../../components/FormField/interfaces';

const englishRegex = /^[a-zA-Z0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;
const arabicRegex = /^[\u0600-\u06FF0-9\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+$/;

const getEditCategorySchema = (t: any) => z.object({
  categoryNameEn: z
    .string()
    .min(1, t('categories.validation.categoryNameRequired'))
    .regex(englishRegex, t('categories.validation.englishRegex'))
    .min(2, t('categories.validation.min2'))
    .max(100, t('categories.validation.max100')),
  categoryNameAr: z
    .string()
    .min(1, t('categories.validation.categoryNameRequired'))
    .regex(arabicRegex, t('categories.validation.arabicRegex'))
    .min(2, t('categories.validation.min2'))
    .max(100, t('categories.validation.max100')),
  categoryIcon: z
    .any()
    .refine((files) => files && files.length > 0, t('categories.validation.categoryImageRequired'))
    .refine(
      (files) => {
        if (!files || files.length === 0) return true;
        const size = files[0]?.originFileObj?.size ?? files[0]?.size;
        return !size || size <= 5 * 1024 * 1024;
      },
      t('categories.validation.maxFileSize5MB')
    ),
  variants: z.array(z.string()).min(1, t('categories.validation.variantRequired')),
});

/**
 * EagerFileField — uploads the file immediately on selection and stores the
 * resulting document ID via onDocumentId. Skips re-upload if the file already
 * has a URL (i.e. was pre-filled from the existing category).
 */
function EagerFileField({
  name,
  uploadFn,
  onDocumentId,
  onUploadStateChange,
  ...fieldProps
}: Omit<field, 'onChangeFile'> & {
  uploadFn: (formData: FormData) => Promise<any>;
  onDocumentId: (id: string) => void;
  onUploadStateChange?: (isUploading: boolean) => void;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const { setValue } = useFormContext();

  const handleChange = async (fileList: any[]) => {
    setValue(name, fileList, { shouldValidate: true });

    const file = fileList?.[fileList.length - 1]?.originFileObj;
    // originFileObj is undefined for pre-filled (existing) files — skip upload
    if (!file) return;

    setIsUploading(true);
    onUploadStateChange?.(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await uploadFn(formData);
      const docId =
        res?.payload?.[0]?.id ||
        res?.payload?.id ||
        res?.id ||
        '';
      onDocumentId(docId);
    } catch {
      onDocumentId('');
    } finally {
      setIsUploading(false);
      onUploadStateChange?.(false);
    }
  };

  return (
    <Spin spinning={isUploading} size="small">
      <FormField
        {...fieldProps}
        name={name}
        onChangeFile={handleChange}
      />
    </Spin>
  );
}

interface EditCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  category: Category;
}

export default function EditCategoryModal({
  isOpen,
  onClose,
  onSuccess,
  category,
}: EditCategoryModalProps) {
  const { t } = useTranslation();
  const [updateOperatorCategory, { isLoading }] = useUpdateOperatorCategoryMutation();
  const [uploadCategoryDocument] = useUploadOperatorCategoryDocumentMutation();

  const [isUploadingDocs, setIsUploadingDocs] = useState(false);

  // Holds the newly-uploaded document ID; starts empty (no change = keep existing on server)
  const categoryDocumentIdRef = useRef<string>('');

  const defaultValues = {
    categoryNameEn: category.nameEn,
    categoryNameAr: category.nameAr,
    categoryIcon: category.iconUrl
      ? [{ uid: '-1', name: 'current_image.png', status: 'done', url: category.iconUrl }]
      : [],
    variants: category.attributeIds ?? [],
  };

  const handleSubmit = async (data: any) => {
    try {
      const payload: Record<string, any> = {
        nameEn: data.categoryNameEn,
        nameAr: data.categoryNameAr,
        attributeIds: data.variants,
      };

      payload.documentId = categoryDocumentIdRef.current || (category as any).documentId;

      await updateOperatorCategory({ id: category.id, body: payload }).unwrap();
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to update category:', error);
      const errorMessage =
        error?.data?.message ||
        error?.data?.errors?.default ||
        error?.data?.errors?.[0] ||
        t('categories.categoryUpdateFailed');
      message.error(errorMessage);
    }
  };

  const editCategorySchema = getEditCategorySchema(t);

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={false}
      title={
        <ModalHeader
          title={t('categories.editCategory')}
          description={t('categories.updateCategoryInfo')}
        />
      }
      centered
      width={600}
    >
      <FormWrapper
        schema={editCategorySchema}
        onSubmit={handleSubmit}
        values={defaultValues}
      >
        <div className="flex flex-col gap-4 mt-4">
          <FormField
            type="text"
            name="categoryNameEn"
            id="categoryNameEn"
            label={t('categories.categoryNameEN')}
            placeholder={t('categories.setName')}
            className="!rounded-lg"
          />
          <FormField
            type="text"
            name="categoryNameAr"
            id="categoryNameAr"
            label={t('categories.categoryNameAR')}
            placeholder={t('categories.setName')}
            className="!rounded-lg"
          />
          <EagerFileField
            type="file"
            name="categoryIcon"
            id="categoryIcon"
            label={t('categories.categoryImage')}
            maxCount={1}
            multiple={false}
            accept="image/png, image/jpeg"
            uploadText={t('categories.dragDropUpload')}
            uploadHints={[
              t('categories.acceptedFileTypes'),
              t('categories.uploadOneFile'),
              t('categories.maxFileSize5MB'),
            ]}
            uploadFn={(fd: FormData) => uploadCategoryDocument(fd).unwrap()}
            onDocumentId={(id) => { categoryDocumentIdRef.current = id; }}
            onUploadStateChange={setIsUploadingDocs}
          />
          <VariantsSelector
            name="variants"
            label={t('categories.chooseVariant')}
          />
        </div>

        <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose} type="button">
            {t('common.cancel')}
          </Button>
          <Button type="submit" loading={isLoading} disabled={isUploadingDocs}>
            {t('categories.saveCategory')}
          </Button>
        </div>
      </FormWrapper>
    </Modal>
  );
}
